/**
 * 서버 약식 디스크립션의 **표시용** 가공.
 *
 * ★ 저장값을 만들지 않는다. 이 파일의 함수는 화면에 그리기 직전에만 부른다.
 *
 *   약식 디스크립션은 표시용 문자열이 아니라 `deal_item.item_description` 에
 *   **저장되는 값 그 자체**다. 서버 구성 페이지가 `buildServerDescription()` 로
 *   만들어 저장 payload 에 싣고(ServerConfigView `buildHeaderSummary`),
 *   백엔드가 그대로 컬럼에 쓴다(deal.service `serverHeaderDescription`).
 *   딜명·검색도 그 컬럼을 읽는다.
 *
 *   그래서 생성기(`shared/server-config/shorthand`)에 문구를 더하면 그 순간부터
 *   저장되는 문자열이 바뀌고, 이미 저장된 행과 형태가 갈린다. 표시만 바꾸고
 *   싶을 때는 생성기를 건드리지 말고 여기를 쓴다 (2026-08-20 회의 06:04·07:01).
 */

/** 샤시 줄 끝에 붙일 말. 회의에서 대소문자까지 못박았다 — `Server`. */
const SERVER_SUFFIX = 'Server';

/**
 * 약식 요약의 **첫 줄(= 샤시 줄)** 에 `Server` 를 넣는다.
 *
 * 자리 (2026-08-21 서안드레): 랙 유닛 괄호가 있으면 **그 앞**이다.
 *
 *   HPE DL380 Gen10 24SFF (2U)   →  HPE DL380 Gen10 24SFF Server (2U)
 *   HPE DL380 Gen11 8LFF         →  HPE DL380 Gen11 8LFF Server
 *
 * 괄호가 없으면 지금처럼 줄 끝에 붙인다. `(2U)` 는 장비 형태이지 모델명의
 * 일부가 아니라 늘 맨 뒤에 온다 — 그 앞이 모델명이 끝나는 자리다.
 *
 * 첫 줄만 손대는 이유: buildServerDescription() 의 줄 순서에서 CHASSIS 가
 * 맨 앞이고 라벨이 없다. 나머지 줄은 `CPU : …` 처럼 라벨이 붙은 구성품이라
 * 여기에 `Server` 가 붙으면 뜻이 어긋난다.
 *
 * 한 줄짜리 문자열(구성 페이지 헤더의 `서버 모델`)이면 그 줄이 곧 샤시 줄이다.
 *
 * ★ 이미 `Server` 라는 낱말이 있으면 그대로 둔다.
 *   예전 판정은 '줄 **끝**이 Server 냐' 였다. 자리를 괄호 앞으로 옮기면 그
 *   판정으로는 못 잡는다 — 카탈로그 원문으로 폴백한 행은
 *   `HPE ProLiant DL380 Gen11 8LFF NC Server (2U)` 처럼 낱말이 가운데 있고,
 *   끝이 `(2U)` 라서 `… Server Server (2U)` 가 된다. 그래서 줄 어디에
 *   있든 본다.
 */
export function withServerSuffix(text: string | null | undefined): string {
  const t = text ?? '';
  if (!t.trim()) return t;

  const nl = t.indexOf('\n');
  const head = nl === -1 ? t : t.slice(0, nl);
  const tail = nl === -1 ? '' : t.slice(nl);

  if (new RegExp(`\\b${SERVER_SUFFIX}\\b`, 'i').test(head)) return t;

  // 랙 유닛 괄호 — `(2U)` `(1U)` `(4.5U)`. 있으면 그 앞이 모델명의 끝이다.
  const m = /^(.*?)(\s*\(\d+(?:\.\d+)?U\))\s*$/i.exec(head);
  const newHead = m
    ? `${m[1]!.trimEnd()} ${SERVER_SUFFIX} ${m[2]!.trim()}`
    : `${head.trimEnd()} ${SERVER_SUFFIX}`;
  return `${newHead}${tail}`;
}
